import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'antd';
import dayjs from 'dayjs';

export default function Header({ history = [] }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openCalendar, setOpenCalendar] = useState(false);
  const [calendarValue, setCalendarValue] = useState(dayjs());

  const handleSignOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : 'U';

  const uploadedDateSet = useMemo(() => {
    const set = new Set();
    if (Array.isArray(history)) {
      history.forEach((item) => {
        const raw = item?.date || item?.uploaded_at;
        if (!raw) return;
        const d = dayjs(raw);
        if (!d.isValid()) return;
        set.add(d.format('YYYY-MM-DD'));
      });
    }
    return set;
  }, [history]);

  const renderDateCell = (value) => {
    const key = value.format('YYYY-MM-DD');
    const hasUpload = uploadedDateSet.has(key);
    const today = dayjs().startOf('day');
    const isPast = value.startOf('day').isBefore(today, 'day');
    const status = hasUpload ? 'uploaded' : isPast ? 'missing' : 'upcoming';

    const styles = {
      uploaded: { bg: '#e8f5e9', border: '#a5d6a7', label: 'Uploaded', labelColor: '#1b5e20' },
      missing: { bg: '#ffebee', border: '#ef9a9a', label: 'Missing', labelColor: '#b71c1c' },
      upcoming: { bg: '#fafafa', border: '#e0e0e0', label: '—', labelColor: '#9e9e9e' },
    };
    const s = styles[status];

    return (
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: s.border,
          bgcolor: s.bg,
          minHeight: 72,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {value.date()}
        </Typography>
        <Box sx={{ mt: 'auto', fontSize: 11, fontWeight: 700, color: s.labelColor }}>{s.label}</Box>
      </Box>
    );
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={1}
      sx={{ borderBottom: '1px solid #eaecef' }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src="/finkraft-logo.png"
            alt="Finkraft"
            sx={{ width: 28, height: 28, mr: 1 }}
          />
          <Typography variant="h6">Finkraft Booking Uploader</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setOpenCalendar(true)}
          >
            Calender View
          </Button>
          <Avatar
            sx={{ width: 28, height: 28, bgcolor: 'primary.main', color: 'white', fontSize: 14 }}
          >
            {initials}
          </Avatar>
          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
            {user?.name}
          </Typography>
          <Button variant="outlined" size="small" endIcon={<LogoutIcon />} onClick={handleSignOut}>
            Sign Out
          </Button>
        </Box>
      </Toolbar>
      <Dialog open={openCalendar} onClose={() => setOpenCalendar(false)} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Uploads Calendar
          <IconButton onClick={() => setOpenCalendar(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: '#e8f5e9',
                  border: '1px solid #a5d6a7',
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Uploaded
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: '#ffebee',
                  border: '1px solid #ef9a9a',
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Missing (past day)
              </Typography>
            </Box>
          </Box>
          <Calendar
            fullscreen
            value={calendarValue}
            mode="month"
            onChange={(v) => setCalendarValue(v)}
            onPanelChange={(v) => setCalendarValue(v)}
            dateFullCellRender={renderDateCell}
          />
        </DialogContent>
      </Dialog>
    </AppBar>
  );
}
